import { useCallback, useId, useRef, useState, type FormEvent } from 'react'
import { ApiError, Btn, DonateAmountPicker, Honeypot, Turnstile, useLocale } from '@kkf/design'
import { submitGift, type GiftReceived } from '../api'
import { DONATE_AMOUNTS } from '../data/donation-details'
import { formatMoney } from '../data/donations'
import { TURNSTILE_SITE_KEY } from '../data/links'
import { useT, type MessageKey } from '../i18n'

export interface GiveFormProps {
  /** The appeal the gift is for, by slug — the value in the page's URL. */
  appeal: string
  /** The appeal's currency, for the confirmation. The API records the gift in it. */
  currency: string
}

/** What `EMAIL_PATTERN` on the API accepts, near enough to say so before the round trip. */
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
/** `MIN_GIFT` and `MAX_GIFT` in `app_api/schemas/donation_schema.py`. */
const MIN_GIFT = 1
const MAX_GIFT = 100000

/**
 * The reader's own words for a refusal, **never the API's `detail`**.
 *
 * The API writes its lines in English, and a Mongolian donor told "This appeal is no longer
 * taking gifts" in English is ground rule 8 broken on the one page where it matters most. So
 * the status — and, for a 409, the `reason` the API puts in `data` — picks a catalogue key.
 * A 422 the form did not catch itself means the two sides disagree about what is valid,
 * which the donor can only fix by trying again, so it is told that plainly.
 */
function failureKey(caught: unknown): MessageKey {
  if (!(caught instanceof ApiError)) return 'donate.error.unreachable'
  const reason = (caught.body?.data as { reason?: string } | null | undefined)?.reason
  if (caught.status === 409 && reason === 'appeal_closed') return 'donate.closed'
  if (caught.status === 404) return 'donate.error.gone'
  if (caught.status === 429) return 'donate.error.tooMany'
  if (caught.status === 400 || caught.status === 403) return 'donate.error.check'
  if (caught.status === 422) return 'donate.error.invalid'
  return 'donate.error.unreachable'
}

type Sending =
  | { state: 'idle' }
  | { state: 'sending' }
  | { state: 'sent'; received: GiftReceived; email: string }
  | { state: 'failed'; message: string }

/**
 * The giving form: who you are, what you want to say, how much — and then the button.
 *
 * ## Used on two pages, so it is here (2026-09-25)
 *
 * The directory's panel on `/donations` and `/donations/:slug` each had their own form — the
 * first with the personal details hidden behind "Read the full appeal", the second the
 * template's, with a three-way payment-method radio and a test-mode notice. Neither posted
 * anything. One form now, in this app's `components/`: two pages of one app are what that
 * directory is for, and a second app wanting it is what would move it to `app_design`.
 *
 * ## The details come first, always visible
 *
 * **Personal info is above the amounts, and is never behind the disclosure.** It was inside
 * "Read the full appeal" until 2026-09-25, so most donors never saw it — and a gift the
 * Foundation cannot write back about is a gift it cannot receipt. The order is the order a
 * person decides in: who am I, am I named, what do I want to say, how much.
 *
 * The note is optional and free text, and it is shown to staff only.
 *
 * ## It claims nothing until the API answers
 *
 * The confirmation shows only once `POST /v1/donations/gifts` resolves, with the reference the
 * API minted. A donor who believes a gift was recorded stops trying to give it, so a failure
 * keeps everything they typed and shows the API's own `detail` line.
 *
 * **Nothing is charged, and the confirmation says so.** The API records a pledge and a PayPal
 * payment with no order behind it; somebody from the Foundation writes back. When PayPal is
 * wired, this is where its window opens.
 *
 * Both anti-spam measures are here, as on every unauthenticated form: the honeypot and, when a
 * site key is configured, Turnstile. The API checks both.
 */
export function GiveForm({ appeal, currency }: GiveFormProps) {
  const t = useT()
  const { locale, intlLocale } = useLocale()
  const id = useId()

  const [amount, setAmount] = useState<number | null>(DONATE_AMOUNTS[0] ?? null)
  const [anonymous, setAnonymous] = useState(false)
  const [sending, setSending] = useState<Sending>({ state: 'idle' })
  const [invalid, setInvalid] = useState<{ email?: string; amount?: string }>({})
  const [token, setToken] = useState<string | null>(null)
  const honeypot = useRef<HTMLInputElement | null>(null)
  // Stable, so the widget is not torn down on every render — see `Turnstile`.
  const onToken = useCallback((next: string | null) => setToken(next), [])

  if (sending.state === 'sent') {
    const { received, email } = sending
    return (
      <div className="ul-dondir-sent" role="status">
        <h3 className="ul-dondir-give-title">{t('donate.sent.title')}</h3>
        <p>
          {t('donate.sent.body', {
            amount: formatMoney(Number(received.amount), received.currency || currency, intlLocale),
            email,
          })}
        </p>
        <p className="ul-dondir-sent-ref">
          {t('donate.sent.reference')} <strong>{received.reference}</strong>
        </p>
      </div>
    )
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    const read = (name: string) => {
      const value = data.get(name)
      return typeof value === 'string' ? value.trim() : ''
    }
    const email = read('email')

    const problems: { email?: string; amount?: string } = {}
    if (!LOOKS_LIKE_EMAIL.test(email)) problems.email = t('donate.error.email')
    if (amount === null || !Number.isFinite(amount) || amount < MIN_GIFT || amount > MAX_GIFT) {
      problems.amount = t('donate.error.amount')
    }
    setInvalid(problems)
    if (problems.email || problems.amount) return

    setSending({ state: 'sending' })
    try {
      const received = await submitGift({
        appeal,
        // Two places, as the API's `Decimal` wants; a float's `0.1 + 0.2` never reaches it.
        amount: (amount as number).toFixed(2),
        first_name: read('first-name') || undefined,
        last_name: read('last-name') || undefined,
        email,
        anonymous,
        note: read('note') || undefined,
        // The page's language, so the Foundation writes back in the one the donor read.
        language: locale,
        kkf_contact_pref: honeypot.current?.value ?? '',
        verification: token ?? '',
      })
      setSending({ state: 'sent', received, email })
    } catch (caught) {
      setSending({ state: 'failed', message: t(failureKey(caught)) })
    }
  }

  return (
    <form action="#" className="ul-give-form" onSubmit={(event) => void submit(event)} noValidate>
      <h3 className="ul-dondir-give-title">{t('donate.personal')}</h3>
      <div className="ul-dondir-fields">
        <input
          type="text"
          name="first-name"
          autoComplete="given-name"
          placeholder={t('form.firstName')}
          aria-label={t('form.firstName')}
        />
        <input
          type="text"
          name="last-name"
          autoComplete="family-name"
          placeholder={t('form.lastName')}
          aria-label={t('form.lastName')}
        />
        <input
          type="email"
          name="email"
          autoComplete="email"
          required
          placeholder={t('form.email')}
          aria-label={t('form.email')}
          aria-invalid={invalid.email ? true : undefined}
          aria-describedby={invalid.email ? `${id}-email-error` : undefined}
        />
      </div>
      {invalid.email && (
        <p className="ul-dondir-error" id={`${id}-email-error`} role="alert">
          {invalid.email}
        </p>
      )}

      {/*
        Give without being named.

        Beside the name fields, because it decides whether those fields are shown to anybody
        — a reader should meet the choice while deciding what to type, not after.
      */}
      <label className="ul-dondir-choice ul-dondir-choice--anon">
        <input
          type="checkbox"
          name="anonymous"
          checked={anonymous}
          onChange={(event) => setAnonymous(event.target.checked)}
        />
        <span>
          {t('donate.anonymous')}
          <small>{t('donate.anonymous.note')}</small>
        </span>
      </label>

      {/* Something to say. Optional, free text, read by staff and nobody else. */}
      <div className="ul-dondir-fields ul-dondir-fields--note">
        <label className="ul-dondir-field-label" htmlFor={`${id}-note`}>
          {t('donate.noteLabel')}
        </label>
        <textarea
          id={`${id}-note`}
          name="note"
          rows={3}
          maxLength={2000}
          placeholder={t('donate.notePlaceholder')}
        />
      </div>

      <h3 className="ul-dondir-give-title ul-dondir-give-title--amount">{t('donate.pick')}</h3>
      {/* `as="div"`: it sits inside this form, and a form inside a form is invalid HTML. */}
      <DonateAmountPicker
        amounts={DONATE_AMOUNTS}
        as="div"
        className="ul-donate-form--panel"
        onChange={setAmount}
      />
      {invalid.amount && (
        <p className="ul-dondir-error" role="alert">
          {invalid.amount}
        </p>
      )}

      <Honeypot
        id={id}
        label={t('donate.honeypot')}
        inputRef={(element) => (honeypot.current = element)}
      />
      <Turnstile
        siteKey={TURNSTILE_SITE_KEY}
        onToken={onToken}
        label={t('donate.turnstile')}
        errorLabel={t('donate.turnstile.error')}
      />

      {sending.state === 'failed' && (
        <p className="ul-dondir-error" role="alert">
          {sending.message}
        </p>
      )}

      <div className="ul-give-form-submit">
        <Btn type="submit" icon="fast-forward-double-right-arrows-symbol" disabled={sending.state === 'sending'}>
          {sending.state === 'sending' ? t('donate.sending') : t('donate.now')}
        </Btn>
      </div>
      <p className="ul-dondir-give-note">{t('donate.note')}</p>
    </form>
  )
}
